import { Loading } from 'components/common/Loading';
import { setOrReplaceAxiosInterceptor } from 'configs/Axios';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  auth,
  signInWithEmail,
  signInWithGoogle,
  signInWithProvider,
} from 'services/firebase';
import { IAuth, UpdateUserProps } from 'types/authentication';
import { errorToastForFirebase, useAuthHooks } from './authHooks';
import { useCommonTranslation } from '../hooks/i18n/useCommonTranslation';
import { SangixDbToFirebaseEmailMismatchHandler } from '../containers/common/auth/SangixDbToFirebaseEmailMismatchHandler';
import { useI18N } from './I18NContext';
import { Language } from '../i18n';
import { AuthProvider as FirebaseAuthProvider } from 'firebase/auth';

const defaultContext: IAuth = {
  errorMessage: null,

  updateAuthUser: () => console.warn('Context not ready'),
  loginEmail: async () => console.warn('Context not ready'),
  loginGoogle: () => console.warn('Context not ready'),
  loginWithProvider: (_) => console.warn('Context not ready'),
  logout: () => {
    console.warn('Context not ready');
    return Promise.resolve();
  },
};

export const AuthContext = React.createContext<IAuth>(defaultContext);
AuthContext.displayName = 'Auth Provider'; // Only for debugging

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC = ({ children }) => {
  const { t } = useCommonTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [userState, setUserState] = useState<IAuth>({
    ...defaultContext,
  });
  const { language, setLanguage } = useI18N();

  const updateUser = (newValues: UpdateUserProps) => {
    setUserState((prev) => ({ ...prev, ...newValues }));
  };

  const { handleFirebaseUserLogin, logout } = useAuthHooks({
    setUserState,
    defaultContext,
  });

  useEffect(() => {
    /**
     * Ensure clearing token and user state when the user has signed off (including
     * in a different tab)
     */
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await handleFirebaseUserLogin(user);
        // Reset guidelines acceptance when user signs in (after sign in he should always see guidelines)
      } else {
        setOrReplaceAxiosInterceptor();
        setUserState(defaultContext);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [setUserState, setIsLoading, handleFirebaseUserLogin]);

  const firebaseLoginError = useCallback(
    (error: any) => {
      setOrReplaceAxiosInterceptor();
      errorToastForFirebase(error, t);
      console.error(error);
      setUserState(defaultContext);
    },
    [setUserState, t],
  );

  const loginEmail = async (email: string, password: string) => {
    try {
      /**
       * side effects handled via onAuthStateChanged
       */
      await signInWithEmail(email, password);
    } catch (error: any) {
      firebaseLoginError(error);
    }
  };
  const loginGoogle = async () => {
    try {
      /**
       * side effects handled via onAuthStateChanged
       */
      await signInWithGoogle();
    } catch (error: any) {
      firebaseLoginError(error);
    }
  };
  const loginWithProvider = async (provider: FirebaseAuthProvider) => {
    try {
      /**
       * side effects handled via onAuthStateChanged
       */
      await signInWithProvider(provider);
    } catch (error: any) {
      firebaseLoginError(error);
    }
  };

  // Set i18n language to the user's language when the user's language changes.
  useEffect(() => {
    const userLanguage = userState?.sangixUser?.language;
    if (userLanguage && userLanguage !== language) {
      try {
        setIsLoading(true);
        setLanguage(userLanguage as Language);
      } finally {
        setIsLoading(false);
      }
    }
  }, [userState?.sangixUser?.language, language, setLanguage, setIsLoading]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <AuthContext.Provider
            value={{
              ...userState,
              updateAuthUser: updateUser,
              loginEmail,
              loginGoogle,
              loginWithProvider,
              logout,
            }}
          >
            <SangixDbToFirebaseEmailMismatchHandler />
            {children}
          </AuthContext.Provider>
        </>
      )}
    </>
  );
};
